import {lazy, memo, Suspense, useEffect, useRef} from 'react';

import {useCloseLegend, useIsLegendEnabled, useIsLegendOpen, useSetIsLegendEnabled} from './store';

const LegendComponent = lazy(() =>
    import(/* webpackChunkName: "Legend" */ './LegendComponent').then((module) => ({
        default: module.LegendComponent,
    }))
);

interface LegendProps {
    map: mapboxgl.Map | undefined;
    layers: Layer[] | undefined;
    layerTypes: Record<string, LayerType> | undefined;
}

export const Legend = memo(function Legend({map, layers, layerTypes}: LegendProps) {
    const isLegendOpen = useIsLegendOpen();
    const closeLegend = useCloseLegend();
    const isLegendEnabled = useIsLegendEnabled();
    const setIsLegendEnabled = useSetIsLegendEnabled();

    // In the future, if `layers` is in atomic state, `isLegendEnabled` would simply be a derived
    // atom; `isLegendOpen` would also be a derived atom from a boolean primitive atom and
    // `isLegendEnabled`. Until then, we need an effect.
    useEffect(() => {
        if (!layers) return;
        const enabled = layers.some((l) => l.type.startsWith('GaiaWinter'));
        setIsLegendEnabled(enabled);
        if (!enabled) closeLegend(); // no analytics when closing programmatically
    }, [layers, closeLegend, setIsLegendEnabled]);

    // The legend does not need to react to the URLs changing; its value is only read by an event
    // handler. Therefore, we can store the array in a ref, and update it with an effect.
    const fullLegendURLsRef = useRef<string[]>([]);

    useEffect(() => {
        if (!layers || !layerTypes) return;
        const supportedLayers = layers.filter((l) => l.type.startsWith('GaiaWinter')) ?? [];
        fullLegendURLsRef.current = supportedLayers
            .map((l) => layerTypes[l.type].legendURL)
            .filter((url): url is string => typeof url === 'string' && url !== '');
    }, [layers, layerTypes]);

    if (!isLegendEnabled || !isLegendOpen) return null;

    return (
        <Suspense fallback={null}>
            <LegendComponent map={map} fullLegendURLsRef={fullLegendURLsRef} />
        </Suspense>
    );
});
